import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { MdClose, MdOutlineDeleteOutline } from 'react-icons/md';
import {
  deletePartyHallImage,
  fetchPartyHallImages,
  fetchPartyHallsByRestaurant,
  uploadPartyHallImage,
} from './partyHallQuoteApi';
import { confirmAction } from './ui/feedback';

const MAX_PARTY_HALL_IMAGES = 5;

function getActorId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return Number(user?.userId ?? user?.User_Id ?? user?.user_id ?? 0);
  } catch (e) {
    return 0;
  }
}

function PartyHallImages({ partyHallId: partyHallIdProp, restaurantId, status, canEdit = true }) {
  const [resolvedHallId, setResolvedHallId] = useState(partyHallIdProp || null);
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isImageFormVisible, setIsImageFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const inactive = String(status || '').toUpperCase() === 'INACTIVE';
  const hallId = resolvedHallId || partyHallIdProp || null;
  const remainingSlots = Math.max(0, MAX_PARTY_HALL_IMAGES - images.length);

  const loadImages = useCallback(async (id) => {
    if (!id) {
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchPartyHallImages(id);
      setImages(rows);
    } catch (error) {
      console.error('Error fetching party hall images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function resolveHallId() {
      if (partyHallIdProp) {
        if (!cancelled) setResolvedHallId(Number(partyHallIdProp));
        return;
      }
      if (!restaurantId) {
        if (!cancelled) setResolvedHallId(null);
        return;
      }
      try {
        const halls = await fetchPartyHallsByRestaurant(restaurantId);
        const first = Array.isArray(halls) ? halls[0] : null;
        const id = first?.partyHallId ?? first?.Party_Hall_Id ?? null;
        if (!cancelled) setResolvedHallId(id != null ? Number(id) : null);
      } catch (error) {
        console.warn('Could not resolve party hall id for images:', error);
        if (!cancelled) setResolvedHallId(null);
      }
    }
    resolveHallId();
    return () => {
      cancelled = true;
    };
  }, [partyHallIdProp, restaurantId]);

  useEffect(() => {
    loadImages(hallId);
  }, [hallId, loadImages]);

  useEffect(() => {
    return () => {
      newImages.forEach((image) => {
        if (image.preview) URL.revokeObjectURL(image.preview);
      });
    };
  }, [newImages]);

  const handleImageFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    setNewImages((current) => {
      const slotsLeft = Math.max(0, MAX_PARTY_HALL_IMAGES - images.length - current.length);
      if (slotsLeft <= 0) {
        alert(`You can only upload ${MAX_PARTY_HALL_IMAGES} images per party hall.`);
        return current;
      }
      const accepted = files.slice(0, slotsLeft);
      if (accepted.length < files.length) {
        alert(
          `Only ${slotsLeft} more image(s) allowed (max ${MAX_PARTY_HALL_IMAGES} per party hall).`
        );
      }
      const imageObjects = accepted.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...current, ...imageObjects];
    });
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((current) => {
      const next = current.filter((_, i) => i !== index);
      if (current[index]?.preview) URL.revokeObjectURL(current[index].preview);
      return next;
    });
  };

  const handleUploadImages = async () => {
    if (newImages.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }
    if (!hallId) {
      alert('Party hall is not available yet. Save party hall details first.');
      return;
    }
    if (images.length + newImages.length > MAX_PARTY_HALL_IMAGES) {
      alert(`You can only upload ${MAX_PARTY_HALL_IMAGES} images per party hall.`);
      return;
    }
    const uploadedById = getActorId();
    if (!uploadedById) {
      alert('Error: Missing user id for upload');
      return;
    }

    setUploading(true);
    try {
      for (const image of newImages) {
        await uploadPartyHallImage({
          partyHallId: hallId,
          image: image.file,
          uploadedById,
        });
      }
      newImages.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
      setNewImages([]);
      setIsImageFormVisible(false);
      await loadImages(hallId);
      alert('Images uploaded successfully!');
    } catch (error) {
      console.error('Error uploading party hall images:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message;
      if (errorMessage && errorMessage.includes('Image upload limit reached')) {
        alert(errorMessage);
      } else {
        alert(errorMessage || 'Error uploading images. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    const ok = await confirmAction({
      title: 'Delete image?',
      message: 'Are you sure you want to delete this image?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deletePartyHallImage(imageId);
      await loadImages(hallId);
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting party hall image:', error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Error deleting image. Please try again.'
      );
    }
  };

  if (!hallId && !restaurantId) return null;

  return (
    <div style={{ marginTop: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px',
        }}
      >
        <h5 style={{ margin: 0 }}>
          Party Hall Images
          {hallId ? (
            <span style={{ fontWeight: 400, color: '#666', fontSize: '14px', marginLeft: '8px' }}>
              ({images.length}/{MAX_PARTY_HALL_IMAGES})
            </span>
          ) : null}
        </h5>
        {canEdit && !inactive ? (
          <Button
            variant="outline-dark"
            onClick={() => {
              if (!isImageFormVisible && remainingSlots <= 0) {
                alert(
                  `Image upload limit reached. Maximum ${MAX_PARTY_HALL_IMAGES} images allowed per party hall.`
                );
                return;
              }
              setIsImageFormVisible((v) => !v);
            }}
            disabled={!hallId || uploading}
            style={{
              width: '90px',
              backgroundColor: !hallId ? '#e9ecef' : '#FFFEF0',
              border: '1px solid #0976A1',
              color: !hallId ? '#6c757d' : '#017EF3',
              cursor: !hallId ? 'not-allowed' : 'pointer',
            }}
          >
            {isImageFormVisible ? 'Cancel' : 'Add'}
          </Button>
        ) : null}
      </div>

      {!hallId ? (
        <p className="text-muted" style={{ marginBottom: 0 }}>
          Save party hall details first to upload images.
        </p>
      ) : null}

      {isImageFormVisible && canEdit && !inactive && hallId ? (
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '20px',
            marginBottom: '20px',
            backgroundColor: '#f9f9f9',
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label>Select Images</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageFileChange}
              disabled={uploading || remainingSlots <= 0}
            />
            <Form.Text className="text-muted">
              Maximum {MAX_PARTY_HALL_IMAGES} images. {remainingSlots} slot(s) remaining.
            </Form.Text>
          </Form.Group>

          {newImages.length > 0 ? (
            <div className="row mt-3">
              {newImages.map((image, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <div className="card" style={{ padding: '10px', position: 'relative' }}>
                    {image.preview ? (
                      <div style={{ position: 'relative' }}>
                        <img
                          src={image.preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            marginBottom: '10px',
                            borderRadius: '4px',
                          }}
                        />
                        <MdClose
                          onClick={() => handleRemoveNewImage(index)}
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '4px',
                            fontSize: '20px',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {newImages.length > 0 ? (
            <Button
              variant="primary"
              onClick={handleUploadImages}
              disabled={uploading}
              style={{ backgroundColor: '#017EF3', border: 'none', marginTop: '10px' }}
            >
              {uploading ? 'Uploading…' : 'Upload Images'}
            </Button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="text-muted">Loading images…</p>
      ) : images.length > 0 ? (
        <div className="row">
          {images.map((image, index) => (
            <div key={image.imageId || index} className="col-md-4 mb-3">
              <div className="card" style={{ padding: '10px', position: 'relative' }}>
                {(image.imageUrl || image.imagePath) && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={image.imageUrl || image.imagePath}
                      alt={image.imageName || `Image ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '10px',
                      }}
                    />
                    {canEdit && !inactive ? (
                      <MdOutlineDeleteOutline
                        onClick={() => handleDeleteImage(image.imageId)}
                        title="Delete image"
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          borderRadius: '50%',
                          padding: '4px',
                          fontSize: '22px',
                          cursor: 'pointer',
                          zIndex: 10,
                        }}
                      />
                    ) : null}
                  </div>
                )}
                {image.imageName ? (
                  <div style={{ fontSize: '12px', color: '#555' }}>{image.imageName}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : hallId ? (
        <p className="text-muted" style={{ marginBottom: 0 }}>
          No images uploaded yet.
        </p>
      ) : null}
    </div>
  );
}

export default PartyHallImages;
